import { useCallback, useEffect, useState } from "react";

import { getUsersStats } from "../api/endpoints";
import { describeError } from "../api/errors";
import type { StatsPeriod, UsersStats } from "../api/types";

interface UsersStatsState {
  stats: UsersStats | null;
  loading: boolean;
  error: string | null;
}

export interface UseUsersStats extends UsersStatsState {
  period: StatsPeriod;
  setPeriod: (period: StatsPeriod) => void;
  retry: () => void;
}

/**
 * The statistics of one window, fetched again whenever the window changes.
 *
 * No polling: the screen is not kept mounted, so opening the tab again is the refresh. A
 * switch of the window cancels the request still in flight - otherwise a slow year arriving
 * after a quick day would draw twelve months under the "Сутки" chip.
 */
export function useUsersStats(): UseUsersStats {
  // a month by day is the most telling of the three while there is little traffic
  const [period, setPeriod] = useState<StatsPeriod>("month");
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<UsersStatsState>({ stats: null, loading: true, error: null });

  useEffect(() => {
    const controller = new AbortController();

    // what is on the screen stays there, dimmed, until the answer replaces it: the charts keep
    // their place instead of collapsing into a loading line and back
    setState((previous) => ({ ...previous, loading: true, error: null }));

    getUsersStats(period, controller.signal).then(
      (stats) => {
        if (!controller.signal.aborted) {
          setState({ stats, loading: false, error: null });
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          // the bars of another window are no answer to this one, so they go with the failure
          setState((previous) => ({
            stats: previous.stats?.period === period ? previous.stats : null,
            loading: false,
            error: describeError(error),
          }));
        }
      },
    );

    return () => controller.abort();
  }, [period, attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { ...state, period, setPeriod, retry };
}
